import { buildStepContent } from '../block-builder';
import type { GuideInteractionMetadata, GuideRenderContext, GuideStep } from '../types';
import { buildWalkthroughStepContent } from '../walkthrough-block-builder';
import { BaseRenderer } from './base-renderer';

/** `style.backdrop === false` = modal sin fondo oscuro (la app queda usable). */
function overlayClassOf(step: GuideStep): string {
  return step.style?.backdrop === false
    ? 'veo-modal-overlay veo-modal-overlay--none'
    : 'veo-modal-overlay';
}

/**
 * Renderiza una guía de tipo `modal`: backdrop oscuro + tarjeta centrada.
 * Cierra al clickear el backdrop o presionar Escape.
 *
 * Con `style.backdrop === false` no hay fondo: el overlay es transparente y
 * deja pasar el mouse (solo la tarjeta es interactiva) — la modal se comporta
 * como una tarjeta flotante posicionable (posX/posY/width/height).
 *
 * Single-step normal: consume `guideSteps[0]`. Si el contexto trae `nav`
 * (paso de un walkthrough heterogéneo modal→banner→tooltip), consume
 * `guideSteps[nav.stepIndex]`, muestra botones de navegación y delega
 * tracking/avance al controller (backdrop/Esc → `nav.callbacks.onSkip`).
 */
export class ModalRenderer extends BaseRenderer {
  private overlay: HTMLElement | null = null;

  render(context: GuideRenderContext): void {
    const idx = context.nav?.stepIndex ?? 0;
    const step = context.guide.guideSteps[idx];
    if (!step) return;

    const { root } = this.createHost();
    this.applyDesign(step.style);
    const ownerDocument = root.ownerDocument ?? document;

    const overlay = ownerDocument.createElement('div');
    overlay.className = overlayClassOf(step);
    this.overlay = overlay;

    const card = ownerDocument.createElement('div');
    card.className = 'veo-modal-card';

    const dismiss = (
      action: 'dismissed' | 'cta_clicked',
      ctaUrl?: string,
      meta?: GuideInteractionMetadata,
    ) => {
      context.onInteraction({
        guideId: context.guide.guideId,
        stepIndex: idx,
        action,
        ...(meta ? { metadata: meta } : {}),
      });
      if (ctaUrl) window.open(ctaUrl, '_blank', 'noopener,noreferrer');
      context.onClose();
    };
    // En modo navegación el cierre lo maneja el controller vía onSkip.
    const onBackdropOrEsc = (): void => {
      if (context.nav) context.nav.callbacks.onSkip();
      else dismiss('dismissed');
    };

    const content = context.nav
      ? buildWalkthroughStepContent(
          step,
          context.nav.stepIndex,
          context.nav.totalSteps,
          ownerDocument,
          context.nav.callbacks,
        )
      : buildStepContent(step, ownerDocument, {
          onCtaClick: (action, target, meta) => {
            dismiss('cta_clicked', action === 'url' ? target : undefined, meta);
            if (action === 'guide' && target) context.onOpenGuide?.(target);
          },
          onDismiss: () => dismiss('dismissed'),
        });

    card.appendChild(content);
    overlay.appendChild(card);
    root.appendChild(overlay);

    // Update in-place (preview del editor) — solo en modo single-step.
    if (!context.nav) {
      this.liveContainer = card;
      this.liveContent = content;
      this.liveBuild = (s) =>
        buildStepContent(s, ownerDocument, {
          onCtaClick: (action, target, meta) => {
            dismiss('cta_clicked', action === 'url' ? target : undefined, meta);
            if (action === 'guide' && target) context.onOpenGuide?.(target);
          },
          onDismiss: () => dismiss('dismissed'),
        });
    }

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) onBackdropOrEsc();
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onBackdropOrEsc();
    };
    document.addEventListener('keydown', onKey);
    this.registerCleanup(() => document.removeEventListener('keydown', onKey));

    // El `shown` de un walkthrough lo emite el controller (step 0) / step_advanced.
    if (!context.nav) {
      context.onInteraction({ guideId: context.guide.guideId, stepIndex: 0, action: 'shown' });
    }
  }

  protected override onLiveUpdate(step: GuideStep): void {
    // El toggle de backdrop desde el editor aplica en vivo, sin remontar.
    if (this.overlay) this.overlay.className = overlayClassOf(step);
  }

  override destroy(): void {
    this.overlay = null;
    super.destroy();
  }
}
